import { PlusIcon, ArchiveIcon } from "lucide-react";
import { useCallback, useContext, useEffect, useState } from "react";
import userJobApi from "@/api/userJob";
import { Button } from "@/components/Button/Button";
import { FeedbackSurveyButton } from "@/components/Feedback/FeedbackSurveyButton";
import { ConfirmModal } from "@/components/Modals/ConfirmModal/ConfirmModal";
import { ArchivedJobsModal } from "@/components/Modals/Jobs/ArchivedJobsModal.tsx";
import JobResumesModal from "@/components/Modals/Jobs/JobResumesModal";
import { PageFooter } from "@/components/PageFooter/PageFooter";
import { PageHelmet } from "@/components/PageHelmet/PageHelmet";
import { AppText } from "@/components/Text/AppText";
import { EditorContext } from "@/context/EditorContext";
import { useAppDispatch } from "@/hooks/types";
import { useInvalidateResumesCache } from "@/hooks/useAppData";
import { UserJobsTable } from "@/pages/UserJobs/UserJobsTable";
import { resetResumeBuilderState } from "@/routes/utils/resetResumeBuilderState";
import { openModal } from "@/store/modal/slice";
import { EditorContextType } from "@/types/editor";
import { ModalTypes } from "@/types/modal";
import { CLARITY_EVENT_NAMES } from "@/types/tracking";
import { trackClarityEvent, trackPostHogEvent } from "@/utils/tracking";

export const UserJobsPage = () => {
    const { destroyEditors } = useContext(EditorContext) as EditorContextType;
    const dispatch = useAppDispatch();
    const invalidateResumes = useInvalidateResumesCache();

    const handleCreateNewJob = () => {
        trackClarityEvent(CLARITY_EVENT_NAMES.NEW_USER_JOB_BUTTON_CLICKED);
        trackPostHogEvent(CLARITY_EVENT_NAMES.NEW_USER_JOB_BUTTON_CLICKED, {
            feature_name: "user_job",
        });
        dispatch(openModal({ modalType: ModalTypes.MANAGE_USER_JOB }));
    };

    const openArchivedJobsModal = useCallback(() => {
        dispatch(openModal({ modalType: ModalTypes.ARCHIVED_JOBS }));
    }, [dispatch]);

    useEffect(() => {
        // Ensure we kill all editors and reset the builder when navigating between resumes
        destroyEditors();
        resetResumeBuilderState(dispatch);
    }, [destroyEditors, dispatch]);

    useEffect(() => {
        invalidateResumes();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <PageHelmet title="Your Jobs" />
            <main className="mt-6 min-h-[calc(100vh-300px)] px-8 pb-16 lg:mt-16">
                <div className="mx-0 mb-6 flex w-full max-w-6xl flex-col justify-between px-0 lg:mx-auto lg:mb-12 lg:flex-row lg:px-20 xl:mb-16 xl:w-2/3 xl:px-0">
                    <AppText
                        variant="headings"
                        className="hidden text-neutral-900 md:block"
                    >
                        Your Jobs
                    </AppText>
                    <div className="mt-0 flex flex-col-reverse items-center gap-3 md:mt-6 lg:my-0 lg:flex-row">
                        <div className="flex flex-row-reverse items-center gap-2 lg:flex-row">
                            <Button
                                color="softIndigo"
                                onClick={openArchivedJobsModal}
                                leftIcon={<ArchiveIcon size={16} />}
                            >
                                Archive
                            </Button>
                            <Button
                                leftIcon={<PlusIcon size={16} />}
                                onClick={handleCreateNewJob}
                            >
                                New Job
                            </Button>
                        </div>
                    </div>
                </div>
                <div className="mx-0 mb-6 flex w-full max-w-6xl flex-col justify-between gap-4 px-0 lg:mx-auto lg:mb-12 lg:px-20 xl:mb-16 xl:px-0">
                    <UserJobsTable showArchived={false} />
                </div>
                <FeedbackSurveyButton />
            </main>
            <PageFooter />
            <JobResumesModal />
            <ConfirmModal />
            <ArchivedJobsModal />
        </>
    );
};
